import { pdf } from "@react-pdf/renderer";
import imageCompression from "browser-image-compression";
import { Order } from "models/Order";
import checklistsApi from "app/api/checklists";
import { store } from "app/store";
import { Checklist } from "models/Checklist";
import { MediaEntity } from "models/MediaEntity";
import ordersApi from "app/api/orders";
import PdfDocument from "features/final-report/PdfDocument";
import { Language } from "./localization/types";

export const IMAGE_QUALITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export type ImageQuality = typeof IMAGE_QUALITY[keyof typeof IMAGE_QUALITY];

const fetchChecklists = async (order: Order) => {
  const promises = order.checklists.filter((checklist) => !checklist.excludedFromReport).map((checklist) =>
    store.dispatch(checklistsApi.endpoints.getChecklist.initiate(checklist.id))
  );
  const results = await Promise.all(promises.map((promise) => promise.unwrap()));
  promises.forEach((promise) => promise.unsubscribe());

  return results.filter((checklist) => !checklist.excludedFromReport);
};

const fetchMediaEntities = async (checklists: Checklist[]) => {
  const promise = store.dispatch(ordersApi.endpoints.getAllIncludedImages.initiate(checklists));
  const results = await promise.unwrap();
  promise.unsubscribe();

  return results;
};

const fetchImageFile = async (imageUrl: string) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const fileName = "image.jpg"; // Adjust the file name as needed
    const imageFile = new File([blob], fileName, {
      type: "image/jpeg",
    });
    return imageFile;
  } catch (error) {
    console.error("Image fetch error:", error);
    return null;
  }
};

export const createPdf = async (
  order: Order,
  imageQuality: ImageQuality,
  language: Language
) => {
  const checklists = await fetchChecklists(order);
  const media = await fetchMediaEntities(checklists);

  // Compress the images before passing them to PDFDocument
  const compressedImages: MediaEntity[] = [];

  for (const image of media) {
    try {
      const imageFile = await fetchImageFile(image.imgUrl);
      if (!imageFile) {
        compressedImages.push(image);
        continue;
      }

      const maxSizeMB =
        imageQuality === IMAGE_QUALITY.MEDIUM ? 0.25
          : imageQuality === IMAGE_QUALITY.HIGH ? 1.0
            : 0.01;

      const compressedImage = await imageCompression(imageFile, {
        maxSizeMB,
        maxWidthOrHeight: 2200,
      });

      compressedImages.push({ ...image, compressedImage });
    } catch (error) {
      console.error("Image compression error:", error);
      compressedImages.push(image);
    }
  }

  // Generate PDF with compressed images
  const blob = await pdf(PdfDocument(order, compressedImages, language)).toBlob();

  return blob;
};
